import { useState, useEffect } from "react";
import { Input, Select, Tooltip } from "antd";

import { DeleteOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";

import { generateYearOptions } from "@/composable/useDates";
import { removeFromList } from "@/utils/list";
import { storeUser } from "@/store/user";

import ExclusiveBadge from "@/components/blocks/ExclusiveBadge";
import VoiceoverReorder from "@/components/voiceover/VoiceoverReorder";
import ModalVoiceover from "@/components/voiceover/ModalVoiceover";

import "./styles/serie-card-edit.scss";

interface Props {
  data: any;
  serialId?: number | string;
  seasonId?: number | string;

  update: (key: any, value: any) => void;
  updateVoiceovers?: (data: any) => void;
  updateDeleteVoiceovers?: (data: any) => void;
  onVoiceoversChanged?: () => void;
}

const { TextArea } = Input;

const SerieCardEdit = ({
  data,
  update,
  updateVoiceovers,
  updateDeleteVoiceovers,
  serialId,
  seasonId,
  onVoiceoversChanged,
}: Props) => {
  const [listVoiceovers, setListVoiceovers] = useState(data.voiceovers);
  const [name, setName] = useState<string>(data.title || "");
  const [description, setDescription] = useState<string>(
    data.description || ""
  );
  const [imdb_id, set_imdb_id] = useState(data.imdb_id || null);
  const [year, setYear] = useState(data.release_year || null);
  const [sort_order, setOrder] = useState<string>(data.sort_order || "");
  const [activeVoiceover, setActiveVoiceover] = useState(null);
  const [kinopoisk_id, setKinopoisk_id] = useState(data.kinopoisk_id || "");

  const [isShowModalVoiceover, setISShowModalVoiceover] = useState(false);
  const [editIndex, setEditIndex] = useState(0);
  const [isReorderVisible, setIsReorderVisible] = useState(false);

  const showVoiceover = (item, index) => {
    setActiveVoiceover(item);
    setISShowModalVoiceover(true);
    setEditIndex(index);
  };

  const updateSerieFile = (data) => {
    const list = listVoiceovers;
    const itemNew = listVoiceovers[editIndex];

    setListVoiceovers((prevSeasons) =>
      prevSeasons.filter((_, i) => i !== editIndex)
    );
    setEditIndex(0);
    updateDeleteVoiceovers(data);
  };

  const handleSaveVoiceover = (data, type) => {
    if (type !== "edit") {
      const newItem = {
        ...data,
        isNew: true,
        active: data.isActive ? 1 : 0,
      };

      setListVoiceovers((prev) => [...prev, newItem]);
    } else {
      // Редактирование существующей озвучки: обновляем рендеримый список
      // (новый массив, чтобы useEffect → updateVoiceovers сработал)
      const list = listVoiceovers.map((item) => {
        if (item.id === data.id) {
          return {
            ...item,
            name: data.name,
            voiceover_id: data.voiceover_id ?? item.voiceover_id,
            video_file: data.video_file ?? item.video_file,
            has_internal_ads: data.has_internal_ads ?? item.has_internal_ads,
          };
        }

        return item;
      });

      setListVoiceovers(list);
    }

    setISShowModalVoiceover(false);
  };

  const handleVoiceoverClose = () => {
    setISShowModalVoiceover(false);
  };

  const showModalFiles = () => {
    setISShowModalVoiceover(true);
  };

  const handleDeleteVoiceover = (voiceover, index) => {
    const list = removeFromList(listVoiceovers, index);
    setListVoiceovers(list);
    updateDeleteVoiceovers(voiceover);
  };

  const handleChangeYear = (e) => {
    let val = null
    if(e) {
      val = e
    }

    setYear(val);
    update("release_year", val);
  }

  const handleToggleVoiceover = (id: string | number) => {
    setListVoiceovers((prevList) =>
      prevList.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            active: Number(item.active) === 1 ? 0 : 1
          };
        }
        return item;
      })
    );
  };

  useEffect(() => {
    updateVoiceovers(listVoiceovers);
  }, [listVoiceovers]);

  return (
    <div>
      <div className="serie-form__container">
        <div className="serie-form__info-content">
          <div className="form-group">
            <label className="form-group__label">
              Название  <span className="app-required">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                update("name", e.target.value);
              }}
              placeholder="Введите название эпизода"
            />
          </div>

          <div className="form-group">
            <label className="form-group__label">Порядок</label>
            <Input
              value={sort_order}
              onChange={(e) => {
                setOrder(e.target.value);
                update("sort_order", e.target.value);
              }}
              placeholder="Введите порядок"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-group__label">
            Описание
          </label>
          <TextArea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              update("description", e.target.value);
            }}
            rows={4}
            placeholder="Описание"
          />
        </div>

        <div className="form-group">
          <label className="form-group__label">
            IMDB ID
          </label>
          <Input
            value={imdb_id}
            onChange={(e) => {
              set_imdb_id(e.target.value);
              update("imdb_id", e.target.value);
            }}
            placeholder="IMDB ID"
          />
        </div>

        <div className="form-group">
          <label className="form-group__label">
            Knopoisk ID
          </label>
          <Input
            value={kinopoisk_id}
            onChange={(e) => {
              setKinopoisk_id(e.target.value);
              update("kinopoisk_id", e.target.value);
            }}
            placeholder="Knopoisk ID"
          />
        </div>

        <div className="form-group">
          <label className="form-group__label" htmlFor={"Year"}>
            Год выпуска
          </label>
          <Select
            id={"Year"}
            className={"app-select"}
            value={year}
            onChange={(e) => {
              handleChangeYear(e)
            }}
            placeholder="Выберите год"
            options={generateYearOptions()}
            style={{ width: 300 }}
            allowClear
          />
        </div>

        <div className="serie-form__voiceovers">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="app-btn" onClick={showModalFiles}>
              Добавить озвучку
            </button>
            {serialId && seasonId && listVoiceovers.length > 1 && storeUser.isSuperAdmin && (
              <button className="app-btn secondary" onClick={() => setIsReorderVisible(true)}>
                Порядок
              </button>
            )}
          </div>
          <ul className="serie-form__list">
            <li
              key="list-static-header-row"
              className="serie-form__list-header"
            >
              <div>
                <Tooltip title="Отображать в плеере" placement="top">
                  <InfoCircleOutlined className="serie-form__header-info" />
                </Tooltip>
              </div>
            </li>

            {listVoiceovers.map((voiceover, index) => {
              return (
                <li
                  key={voiceover.id}
                  className="serie-form__voiceovers-item"
                >
                  <input
                    type="checkbox"
                    name="status"
                    className="serie-form__checkbox"
                    checked={Number(voiceover.active) === 1}
                    onChange={() => handleToggleVoiceover(voiceover.id)}
                  />
                  <span className="serie-form__voiceovers-item__name">
                    {voiceover.name}
                  </span>
                  {voiceover.exclusive_until && (
                    <ExclusiveBadge exclusiveUntil={voiceover.exclusive_until} />
                  )}

                  <div className="serie-form__control">
                    {
                      [0,1].includes(Number(voiceover.status_id)) && <button
                            className="app-btn-icon"
                            onClick={() => showVoiceover(voiceover, index)}
                        >
                          <EditOutlined />
                      </button>
                    }

                    {
                      storeUser.isCanRemove && <button
                            className="app-btn-icon delete"
                            onClick={() => handleDeleteVoiceover(voiceover, index)}
                        >
                          <DeleteOutlined />
                      </button>
                    }
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {isShowModalVoiceover && (
          <ModalVoiceover
            close={handleVoiceoverClose}
            voiceover={activeVoiceover}
            save={handleSaveVoiceover}
            type="serie"
            updateSerie={updateSerieFile}
          />
        )}

        {serialId && seasonId && data.id && (
          <VoiceoverReorder
            voiceovers={listVoiceovers.map((v: any) => ({
              video_id: v.video_id,
              name: v.name || '',
              sort_order: v.sort_order ?? null,
            }))}
            visible={isReorderVisible}
            serialId={serialId}
            seasonId={seasonId}
            episodeId={data.id}
            onClose={() => setIsReorderVisible(false)}
            onSaved={() => onVoiceoversChanged?.()}
          />
        )}
      </div>
    </div>
  );
};

export default SerieCardEdit;
